import { useEffect, useMemo, useRef, useState } from "react";
import { geoIdentity, geoPath } from "d3-geo";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Plus, Minus, RotateCcw } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { fetchGeo, seatColor } from "@/lib/insights";

const W = 600;
const H = 680;

function ZoomControls({ zoomIn, zoomOut, resetTransform }) {
  const { t } = useLang();
  const btn = "w-9 h-9 grid place-items-center rounded-full bg-white shadow-md text-[#1a1640] hover:bg-[#f3eedc] active:scale-95 transition-transform";
  return (
    <div className="absolute right-3 top-3 z-10 flex flex-col gap-2">
      <button className={btn} onClick={() => zoomIn()} aria-label={t("zoom_in")} data-testid="map-zoom-in"><Plus size={16} /></button>
      <button className={btn} onClick={() => zoomOut()} aria-label={t("zoom_out")} data-testid="map-zoom-out"><Minus size={16} /></button>
      <button className={btn} onClick={() => resetTransform()} aria-label={t("zoom_reset")} data-testid="map-zoom-reset"><RotateCcw size={15} /></button>
    </div>
  );
}

export default function TnMap({ seatsByAc, mode, selected, onSelect, focus, district }) {
  const [geo, setGeo] = useState(null);
  const [scale, setScale] = useState(1);
  const apiRef = useRef(null);
  const boxRef = useRef(null);
  const { lang } = useLang();

  useEffect(() => { fetchGeo().then(setGeo); }, []);

  const paths = useMemo(() => {
    if (!geo) return [];
    const projection = geoIdentity().reflectY(true).fitSize([W, H], geo);
    const path = geoPath(projection);
    return geo.features.map((f) => ({ ac: f.properties.ac_no, d: path(f), c: path.centroid(f), b: path.bounds(f) }));
  }, [geo]);

  useEffect(() => {
    if (!apiRef.current || !boxRef.current || !paths.length) return;
    if (!district) { apiRef.current.resetTransform(400); return; }
    const inD = paths.filter((p) => seatsByAc[p.ac]?.district === district);
    if (!inD.length) return;
    const x0 = Math.min(...inD.map((p) => p.b[0][0])), y0 = Math.min(...inD.map((p) => p.b[0][1]));
    const x1 = Math.max(...inD.map((p) => p.b[1][0])), y1 = Math.max(...inD.map((p) => p.b[1][1]));
    const cw = boxRef.current.offsetWidth;
    const s = cw / W;
    const k = Math.min(12, Math.max(1, 0.85 * Math.min(W / (x1 - x0), H / (y1 - y0))));
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    apiRef.current.setTransform(cw / 2 - cx * s * k, (H * s) / 2 - cy * s * k, k, 400);
  }, [district, paths, seatsByAc]);

  useEffect(() => {
    const p = focus && paths.find((x) => x.ac === focus.ac);
    if (!p || !apiRef.current || !boxRef.current) return;
    const cw = boxRef.current.offsetWidth;
    const s = cw / W;
    const size = Math.max(p.b[1][0] - p.b[0][0], p.b[1][1] - p.b[0][1]);
    const k = Math.min(12, Math.max(2, (W * 0.18) / size));
    apiRef.current.setTransform(cw / 2 - p.c[0] * s * k, (H * s) / 2 - p.c[1] * s * k, k, 400);
  }, [focus, paths]);

  if (!geo) return <div className="aspect-[600/680] w-full rounded-[22px] bg-white/60 animate-pulse" data-testid="map-loading" />;

  return (
    <div ref={boxRef} className="relative rounded-[22px] bg-[#eef3fb] overflow-hidden" data-testid="tn-map">
      <TransformWrapper minScale={1} maxScale={14} doubleClick={{ disabled: true }} ref={apiRef} onTransformed={(_, st) => setScale(st.scale)}>
        {(utils) => (
          <>
            <ZoomControls {...utils} />
            <TransformComponent wrapperStyle={{ width: "100%" }} contentStyle={{ width: "100%" }}>
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img">
                {paths.map(({ ac, d }) => {
                  const seat = seatsByAc[ac];
                  const isSel = selected === ac;
                  return (
                    <path
                      key={ac}
                      id={`ac-${ac}`}
                      d={d}
                      fill={seat ? seatColor(seat, mode) : "#d6d3e6"}
                      stroke="#ffffff"
                      strokeWidth={0.5 / scale}
                      className="cursor-pointer transition-opacity hover:opacity-75"
                      style={{ opacity: district && seat?.district !== district ? 0.15 : selected && !isSel ? 0.82 : 1 }}
                      onClick={() => onSelect(ac)}
                      data-testid={`map-seat-${ac}`}
                    >
                      <title>{seat ? (lang === "ta" ? seat.name_ta : seat.name) : ac}</title>
                    </path>
                  );
                })}
                {selected && paths.find((p) => p.ac === selected) && (
                  <path d={paths.find((p) => p.ac === selected).d} fill="none" stroke="#1a1640" strokeWidth={2 / scale} pointerEvents="none" />
                )}
              </svg>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
}
